
import React from 'react';
import { WeatherData } from '@/types/weather';
import { ImageDownloader } from './sharing/ImageDownloader';
import { 
  shareViaEmail, 
  shareViaWhatsApp, 
  shareViaTelegram, 
  shareViaSMS,
  shareViaWebShare
} from './sharing/SharingUtilities';
import ShareButtonsUI from './sharing/ShareButtonsUI';
import { Button } from '@/components/ui/button';
import { Share2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ShareButtonsProps {
  weatherData: WeatherData;
  imageRef: React.RefObject<HTMLDivElement>;
}

export const ShareButtons: React.FC<ShareButtonsProps> = ({ weatherData, imageRef }) => {
  const imageDownloader = ImageDownloader({ weatherData, imageRef });
  const handleDownload = imageDownloader.handleDownload;
  const { toast } = useToast();

  // Main share handler that uses Web Share API if available
  const handleShare = async () => {
    try {
      const shared = await shareViaWebShare(weatherData, imageRef);
      if (shared) {
        toast({
          title: "Shared successfully",
          description: "Your weather forecast has been shared with image",
        });
      } else {
        // If Web Share API is not available, show regular sharing options
        toast({
          title: "Sharing options",
          description: "Please use the buttons below to share your forecast",
        });
      }
    } catch (error) {
      console.error("Error sharing:", error);
      toast({
        title: "Sharing failed",
        description: "There was an error sharing your forecast",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-3">
      {/* Native share button for platforms with Web Share API */}
      <Button 
        className="w-full flex items-center justify-center gap-2"
        onClick={handleShare}
      >
        <Share2 className="h-4 w-4" />
        Share Forecast
      </Button>

      <ShareButtonsUI
        onDownload={handleDownload}
        onEmailShare={() => shareViaEmail(weatherData, imageRef)}
        onWhatsAppShare={() => shareViaWhatsApp(weatherData, imageRef)}
        onTelegramShare={() => shareViaTelegram(weatherData, imageRef)}
        onSMSShare={() => shareViaSMS(weatherData, imageRef)}
      />
    </div>
  );
};

export default ShareButtons;
